import { FC, ReactNode } from 'react';

import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import {
	PunchupDownloadButton,
	PunchupRefreshButton,
} from 'features/common/components/Punchup/primitives/buttons/PunchupButton';

interface PunchupTablePageHeaderProps {
	/** Page title — must match `pageName` passed to `PunchupPageShell`. */
	title: string;
	/** Muted subtitle explaining the page purpose. */
	description: string;
	/**
	 * Optional page-specific CTA rendered LEFTMOST in the actions group (e.g.
	 * `PunchupSendButton` on Comped Tickets). Use the icon-collapse button
	 * primitives (`PunchupSendButton`, `PunchupUploadButton`, etc.) so the CTA
	 * collapses to icon-only on mobile alongside Download / Refresh.
	 */
	customAction?: ReactNode;
	/**
	 * When provided, renders a Download button to the right of `customAction`.
	 * The label string doubles as the `aria-label` for the icon-only mobile
	 * state.
	 */
	onDownload?: () => void;
	/** Visible label / aria-label for the Download button. Required when `onDownload` is set. */
	downloadLabel?: string;
	/** While a download is in flight, shows a spinner in place of the Download icon and disables the button. */
	isDownloading?: boolean;
	/**
	 * When provided, renders a Refresh button as the rightmost action. Standard
	 * across every table page so the venue manager always knows where to find
	 * the manual refresh affordance.
	 */
	onRefresh?: () => void;
	/** Visible label / aria-label for the Refresh button. Required when `onRefresh` is set. */
	refreshLabel?: string;
	/** While a re-fetch is in flight (`isFetching`), shows a spinner in place of the Refresh icon and disables the button. */
	isRefreshing?: boolean;
}

/**
 * Title block + standard action group for every Punchup table sub-page.
 *
 * Renders `PunchupPageHeader` with a fixed-order action row in the right slot:
 * `customAction → Download → Refresh`. Each button is icon-collapse capable, so
 * the row stays compact on mobile by hiding labels below 768px.
 *
 * The header row supports `flex-wrap`: when title + actions don't fit on one
 * line, the actions wrap to a second right-aligned line directly under the
 * title (see `PunchupPageHeader.module.css`). This makes long titles safe on
 * narrow viewports without truncation.
 *
 * Use this on every table sub-page in place of a hand-rolled `PunchupPageHeader`
 * + button row. Keeps the visual contract uniform across Transactions, Comped,
 * Attendee List, View Responses, Scanned Tickets.
 */
export const PunchupTablePageHeader: FC<PunchupTablePageHeaderProps> = ({
	title,
	description,
	customAction,
	onDownload,
	downloadLabel,
	isDownloading,
	onRefresh,
	refreshLabel,
	isRefreshing,
}) => (
	<PunchupPageHeader
		title={title}
		description={description}
		actions={
			<>
				{customAction}
				{onDownload && downloadLabel && (
					<PunchupDownloadButton onClick={onDownload} isLoading={isDownloading}>
						{downloadLabel}
					</PunchupDownloadButton>
				)}
				{onRefresh && refreshLabel && (
					<PunchupRefreshButton onClick={onRefresh} isLoading={isRefreshing}>
						{refreshLabel}
					</PunchupRefreshButton>
				)}
			</>
		}
	/>
);
